import { useEffect, useState, useCallback } from "react";
import { Download, LogOut, Search, ShieldAlert, Users, Inbox, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const TYPES = ["", "eligibility", "enroll", "tour", "transportation", "callback"];
const STATUSES = ["new", "contacted", "tour-booked", "enrolled", "closed"];
const fmt = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

function LeadDrawer({ id, onClose, onUpdated }) {
  const [lead, setLead] = useState(null);
  useEffect(() => {
    setLead(null);
    if (id) api.get(`/leads/${id}`).then((r) => setLead(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  const setStatus = async (status) => {
    const { data } = await api.patch(`/leads/${id}`, { status });
    setLead(data);
    onUpdated();
  };
  const rows = lead ? [
    ["Form", lead.form_type], ["Scene", lead.scene], ["Phone", lead.phone], ["Email", lead.email], ["Child's age", lead.child_age],
    ["Best time", lead.best_time], ["Care needed", lead.care_need], ["Preferred date", lead.preferred_date], ["Preferred time", lead.preferred_time],
    ["Pickup area", lead.pickup_area], ["Parent status", lead.parent_status],
    ["First time in daycare", lead.first_time_daycare], ["Services wanted", lead.services?.join(", ")],
    ["Notes", lead.notes], ["Received", fmt(lead.created_at)],
  ].filter(([, v]) => v) : [];
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="lead-detail-drawer" className="w-full overflow-y-auto bg-white sm:max-w-md">
        <SheetTitle className="font-display text-2xl">{lead?.parent_name || "Loading…"}</SheetTitle>
        <SheetDescription>Lead detail</SheetDescription>
        {lead && (
          <div className="mt-5 space-y-5">
            <label className="rrb-field"><span>Status</span>
              <select data-testid="lead-status-select" value={lead.status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <dl className="divide-y rounded-2xl border">
              {rows.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[120px_1fr] gap-3 px-4 py-2.5 text-sm">
                  <dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
            {(lead.household_size || lead.income_band || lead.special_needs) && (
              <div data-testid="lead-sensitive-section" className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-800"><ShieldAlert className="h-4 w-4" /> Sensitive — staff only</p>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-slate-500">Household size</p><p data-testid="lead-household-size" className="font-semibold">{lead.household_size || "—"}</p></div>
                  <div><p className="text-slate-500">Income range</p><p data-testid="lead-income-band" className="font-semibold">{lead.income_band || "—"}</p></div>
                  <div className="col-span-2"><p className="text-slate-500">Disabilities / medical needs</p><p data-testid="lead-special-needs" className="font-semibold">{lead.special_needs || "—"}</p></div>
                </div>
                <p className="mt-3 text-xs text-amber-800/80">Not shown in the list or included in downloads.</p>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    api.get("/leads", { params: { q: q || undefined, form_type: type || undefined } })
      .then((r) => setLeads(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [q, type]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const download = async () => {
    const { data } = await api.get("/leads/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "rrb-leads.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="leads-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email"
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-sky-400" />
        </div>
        <select data-testid="leads-type-filter" value={type} onChange={(e) => setType(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm">
          {TYPES.map((t) => <option key={t} value={t}>{t || "All forms"}</option>)}
        </select>
        <button data-testid="leads-export-btn" onClick={download} className="rrb-btn rrb-btn-sky rrb-btn-sm"><Download className="h-4 w-4" /> Download CSV</button>
      </div>
      <div className="mt-5 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
        <table className="w-full min-w-[820px] text-left text-sm" data-testid="leads-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Name", "Contact", "Form", "Scene", "Child's age", "Status", "Date"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {leads === null && <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {leads?.length === 0 && <tr><td colSpan={7} data-testid="leads-empty" className="px-4 py-10 text-center text-slate-500">No leads yet.</td></tr>}
            {leads?.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setOpenId(l.id)} className="cursor-pointer border-t border-slate-100 hover:bg-sky-50/60">
                <td className="px-4 py-3 font-semibold text-slate-800">{l.parent_name}</td>
                <td className="px-4 py-3 text-slate-600">{l.phone}<br /><span className="text-xs">{l.email}</span></td>
                <td className="px-4 py-3"><span className={`rrb-tag tag-${l.form_type}`}>{l.form_type}</span></td>
                <td className="px-4 py-3 text-slate-600">{l.scene}</td>
                <td className="px-4 py-3 text-slate-600">{l.child_age || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{l.status}</td>
                <td className="px-4 py-3 text-slate-500">{fmt(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LeadDrawer id={openId} onClose={() => setOpenId(null)} onUpdated={load} />
    </>
  );
}

function TeamTab({ me }) {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const load = () => api.get("/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", f);
      setF({ name: "", email: "", password: "" });
      toast.success("Staff account created");
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const remove = async (id) => { await api.delete(`/staff/${id}`); load(); };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <ul className="divide-y rounded-3xl border border-slate-200 bg-white" data-testid="staff-list">
        {staff.map((s) => (
          <li key={s.id} className="flex items-center justify-between px-5 py-3.5 text-sm">
            <div><p className="font-semibold">{s.name}</p><p className="text-slate-500">{s.email} · {s.role}</p></div>
            {s.id !== me.id && s.role !== "owner" && (
              <button data-testid={`staff-remove-${s.id}`} onClick={() => remove(s.id)} className="rounded-full p-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            )}
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="add-staff-form">
        <h3 className="font-display text-lg font-semibold">Add a staff member</h3>
        {[["name", "Name", "text"], ["email", "Email", "email"], ["password", "Temporary password (8+ chars)", "password"]].map(([k, l, t]) => (
          <label key={k} className="rrb-field mt-3"><span>{l}</span>
            <input data-testid={`add-staff-${k}-input`} type={t} required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </label>
        ))}
        <button data-testid="add-staff-submit-btn" className="rrb-btn rrb-btn-green mt-4 w-full justify-center">Create account</button>
      </form>
    </div>
  );
}

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  return (
    <div className="rrb-staff min-h-screen">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white/80 px-5 py-3 backdrop-blur sm:px-8">
        <div className="flex items-center gap-3">
          <img src="/media/rrb-logo-sm.webp" alt="RR&B" className="h-12 w-auto" />
          <div><p className="font-display text-lg font-semibold leading-none">Lead inbox</p><p className="text-xs text-slate-500">Temporary · until GoHighLevel is connected</p></div>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="staff-user-email" className="hidden text-sm text-slate-500 sm:inline">{user.email}</span>
          <button data-testid="staff-logout-btn" onClick={logout} className="rrb-btn rrb-btn-sm bg-slate-100 text-slate-700 hover:bg-slate-200"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="mb-6 flex gap-2">
          <button data-testid="tab-leads" onClick={() => setTab("leads")} className={`rrb-tab ${tab === "leads" ? "on" : ""}`}><Inbox className="h-4 w-4" /> Leads</button>
          {user.role === "owner" && (
            <button data-testid="tab-team" onClick={() => setTab("team")} className={`rrb-tab ${tab === "team" ? "on" : ""}`}><Users className="h-4 w-4" /> Team</button>
          )}
        </div>
        {tab === "leads" ? <LeadsTab /> : <TeamTab me={user} />}
      </main>
    </div>
  );
}
